
import React, { useEffect, useMemo, useState } from "react";
import { Clock, Plus, RefreshCw, Save, Trash2 } from "lucide-react";
import { toast } from "react-toastify";

const DAYS = [
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
  "SUNDAY",
];

const SHIFTS = ["1", "2", "3"];

const makeEmptyRow = (day = "MONDAY", shift = "1") => ({
  pk_timing_id: "0",
  restaurant_id: "",
  day_name: day,
  shift_no: shift,
  open_time: "09:00",
  close_time: "23:00",
  is_closed: "0",
  is_24_hours: "0",
});

const toTimeInput = (value) => {
  if (!value) return "";

  const str = String(value).trim();

  const ampmMatch = str.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);

  if (ampmMatch) {
    let hour = Number(ampmMatch[1]);
    const minute = ampmMatch[2];
    const suffix = ampmMatch[3].toUpperCase();

    if (suffix === "PM" && hour !== 12) hour += 12;
    if (suffix === "AM" && hour === 12) hour = 0;

    return `${String(hour).padStart(2, "0")}:${minute}`;
  }

  if (str.length >= 5) {
    return str.slice(0, 5);
  }

  return str;
};

const to12HourLabel = (value) => {
  if (!value) return "—";

  const str = String(value).trim();

  if (str.toUpperCase().includes("AM") || str.toUpperCase().includes("PM")) {
    return str;
  }

  const [hourRaw, minuteRaw] = str.split(":");

  let hour = Number(hourRaw);
  const minute = minuteRaw || "00";

  if (Number.isNaN(hour)) return str;

  const suffix = hour >= 12 ? "PM" : "AM";

  hour %= 12;

  if (hour === 0) {
    hour = 12;
  }

  return `${String(hour).padStart(2, "0")}:${minute} ${suffix}`;
};

const normalizeWeekOff = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => String(item).trim().toUpperCase())
      .filter(Boolean);
  }

  if (!value) return [];

  return String(value)
    .split(",")
    .map((item) => item.trim().toUpperCase())
    .filter(Boolean);
};

const toApiTime = (value) => {
  if (!value) return "00:00:00";

  const time = String(value).trim();

  if (time.length === 5) {
    return `${time}:00`;
  }

  return time;
};

const normalizeTimingRow = (row = {}) => ({
  pk_timing_id: String(row?.pk_timing_id || row?.id || "0"),

  restaurant_id: String(row?.restaurant_id || row?.restaurantId || ""),

  day_name: String(row?.day_name || row?.dayName || "MONDAY").toUpperCase(),

  shift_no: String(row?.shift_no || row?.shiftNo || "1"),

  open_time: toTimeInput(row?.open_time || row?.openTime || "09:00"),

  close_time: toTimeInput(row?.close_time || row?.closeTime || "23:00"),

  is_closed: String(row?.is_closed ?? row?.isClosed ?? "0") === "1" ? "1" : "0",

  is_24_hours:
    String(row?.is_24_hours ?? row?.is24Hours ?? "0") === "1" ? "1" : "0",
});

const uniqueLatestTimings = (list = []) => {
  const timingMap = new Map();

  list.forEach((item) => {
    const row = normalizeTimingRow(item);
    const key = `${row.day_name}-${row.shift_no}`;

    const existing = timingMap.get(key);

    if (!existing) {
      timingMap.set(key, row);
      return;
    }

    const existingId = Number(existing.pk_timing_id || 0);

    const currentId = Number(row.pk_timing_id || 0);

    if (currentId >= existingId) {
      timingMap.set(key, row);
    }
  });

  return Array.from(timingMap.values()).sort((a, b) => {
    const dayDifference = DAYS.indexOf(a.day_name) - DAYS.indexOf(b.day_name);

    if (dayDifference !== 0) {
      return dayDifference;
    }

    return Number(a.shift_no) - Number(b.shift_no);
  });
};

const TimingSettings = ({
  userID,
  getRestaurantTiming,
  updateRestaurantTiming,
  getRestaurantTimingsV2,
  saveRestaurantTimingsV2,
}) => {
  const [timings, setTimings] = useState([]);
  const [rows, setRows] = useState([makeEmptyRow()]);
  const [weekOff, setWeekOff] = useState("");

const [restaurantTimingSummary, setRestaurantTimingSummary] = useState({
  opening: "",
  closing: "",
  weekOff: "",
});

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const groupedTimings = useMemo(() => {
    const map = {};

    DAYS.forEach((day) => {
      map[day] = [];
    });

    timings.forEach((item) => {
      const day = item.day_name;

      if (!map[day]) {
        map[day] = [];
      }

      map[day].push(item);
    });

    Object.keys(map).forEach((day) => {
      map[day].sort((a, b) => Number(a.shift_no) - Number(b.shift_no));
    });

    return map;
  }, [timings]);

  const createRowsFromApiData = (data) => {
    const opening = toTimeInput(
      data?.opening ||
        data?.openingTime ||
        data?.opening_time ||
        data?.open_time ||
        "09:00",
    );

    const closing = toTimeInput(
      data?.closing ||
        data?.closingTime ||
        data?.closing_time ||
        data?.close_time ||
        "23:00",
    );

    const weekOffDays = normalizeWeekOff(
      data?.weekOff || data?.weekoff || data?.week_off || "",
    );

    setWeekOff(weekOffDays[0] || "");

    return DAYS.map((day) => {
      const isWeekOff = weekOffDays.includes(day);

      return {
        pk_timing_id: "0",
        restaurant_id: "",
        day_name: day,
        shift_no: "1",
        open_time: isWeekOff ? "00:00" : opening,
        close_time: isWeekOff ? "00:00" : closing,
        is_closed: isWeekOff ? "1" : "0",
        is_24_hours:
          !isWeekOff && opening === "00:00" && closing === "23:59" ? "1" : "0",
      };
    });
  };

 const loadTimings = async () => {
   if (!userID) {
     toast.error("User ID missing");
     return;
   }

   if (!getRestaurantTimingsV2) {
     toast.error("getRestaurantTimingsV2 function missing");
     return;
   }

   if (!getRestaurantTiming) {
     toast.error("getRestaurantTiming function missing");
     return;
   }

   try {
     setLoading(true);

     // ============================================
     // 1. MANAGE RESTAURANT TIMINGS GET API
     // Day-wise and shift-wise data
     // ============================================
     const manageResult = await getRestaurantTimingsV2(userID);

     if (manageResult?.success) {
       const list = Array.isArray(manageResult.data) ? manageResult.data : [];

       const uniqueList = uniqueLatestTimings(list);

       setTimings(uniqueList);
       setRows(uniqueList.length > 0 ? uniqueList : [makeEmptyRow()]);
     } else {
       setTimings([]);
       setRows([makeEmptyRow()]);

       toast.error(
         manageResult?.message || "Failed to fetch detailed restaurant timings",
       );
     }

     // ============================================
     // 2. GET RESTAURANT TIMING API
     // Opening, closing and weekOff summary
     // ============================================
     const timingResult = await getRestaurantTiming();

     if (timingResult?.success) {
       const responseData = timingResult?.data;

       const timingData = Array.isArray(responseData)
         ? responseData[0] || {}
         : responseData || {};

       const opening = toTimeInput(
         timingData?.opening ||
           timingData?.openingTime ||
           timingData?.opening_time ||
           timingData?.open_time ||
           "",
       );

       const closing = toTimeInput(
         timingData?.closing ||
           timingData?.closingTime ||
           timingData?.closing_time ||
           timingData?.close_time ||
           "",
       );

       const weekOffDays = normalizeWeekOff(
         timingData?.weekOff ||
           timingData?.weekoff ||
           timingData?.week_off ||
           "",
       );

       const fetchedWeekOff = weekOffDays.join(",");

       setWeekOff(weekOffDays[0] || "");

       setRestaurantTimingSummary({
         opening,
         closing,
         weekOff: fetchedWeekOff,
       });
     } else {
       setWeekOff("");

       setRestaurantTimingSummary({
         opening: "",
         closing: "",
         weekOff: "",
       });
     }
   } catch (error) {
     console.error("LOAD RESTAURANT TIMINGS ERROR:", error);

     setTimings([]);
     setRows([makeEmptyRow()]);
     setWeekOff("");

     setRestaurantTimingSummary({
       opening: "",
       closing: "",
       weekOff: "",
     });

     toast.error("Failed to fetch restaurant timings");
   } finally {
     setLoading(false);
   }
 };

useEffect(() => {
  if (userID) {
    loadTimings();
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [userID]);

  const updateRow = (index, key, value) => {
    setRows((prev) =>
      prev.map((row, rowIndex) => {
        if (rowIndex !== index) return row;

        const next = {
          ...row,
          [key]: value,
        };

        if (key === "is_closed" && value === "1") {
          next.is_24_hours = "0";
          next.open_time = "00:00";
          next.close_time = "00:00";
        }

        if (key === "is_closed" && value === "0") {
          next.open_time = row.open_time === "00:00" ? "09:00" : row.open_time;

          next.close_time =
            row.close_time === "00:00" ? "23:00" : row.close_time;
        }

        if (key === "is_24_hours" && value === "1") {
          next.is_closed = "0";
          next.open_time = "00:00";
          next.close_time = "23:59";
        }

        if (key === "is_24_hours" && value === "0") {
          next.open_time = row.open_time === "00:00" ? "09:00" : row.open_time;

          next.close_time =
            row.close_time === "23:59" ? "23:00" : row.close_time;
        }

        return next;
      }),
    );
  };

  const handleWeekOffChange = (selectedDay) => {
    setWeekOff(selectedDay);

    setRows((prev) =>
      prev.map((row) => {
        const isSelectedWeekOff = selectedDay && row.day_name === selectedDay;

        if (isSelectedWeekOff) {
          return {
            ...row,
            is_closed: "1",
            is_24_hours: "0",
            open_time: "00:00",
            close_time: "00:00",
          };
        }

        // Purane selected week-off ko dobara open karo
        if (row.is_closed === "1") {
          return {
            ...row,
            is_closed: "0",
            is_24_hours: "0",
            open_time: "09:00",
            close_time: "23:00",
          };
        }

        return row;
      }),
    );
  };

  const addRow = () => {
    const last = rows[rows.length - 1];

    const sameDayRows = rows.filter((row) => row.day_name === last?.day_name);

    if (sameDayRows.length >= 3) {
      toast.error(`${last?.day_name || "Selected day"} already has 3 shifts`);
      return;
    }

    const nextShift = String(sameDayRows.length + 1);

    setRows((prev) => [
      ...prev,
      makeEmptyRow(last?.day_name || "MONDAY", nextShift),
    ]);
  };

  const removeRow = (index) => {
    if (rows.length === 1) {
      toast.error("At least one timing row is required");
      return;
    }

    setRows((prev) => prev.filter((_, rowIndex) => rowIndex !== index));
  };

  const validateRows = () => {
    const used = new Set();

    for (const row of rows) {
      const key = `${row.day_name}-${row.shift_no}`;

      if (used.has(key)) {
        toast.error(`${row.day_name} shift ${row.shift_no} is added twice`);

        return false;
      }

      used.add(key);

      if (row.is_closed === "1") continue;
      if (row.is_24_hours === "1") continue;

      if (!row.open_time || !row.close_time) {
        toast.error(`Please add open and close time for ${row.day_name}`);

        return false;
      }

      if (row.open_time >= row.close_time) {
        toast.error(
          `${row.day_name} shift ${row.shift_no}: close time must be after open time`,
        );

        return false;
      }
    }

    return true;
  };

const saveTimings = async () => {
  if (!userID) {
    toast.error("User ID missing");
    return;
  }

  if (!saveRestaurantTimingsV2) {
    toast.error("saveRestaurantTimingsV2 function missing");
    return;
  }

  if (!updateRestaurantTiming) {
    toast.error("updateRestaurantTiming function missing");
    return;
  }

  if (!validateRows()) return;

  try {
    setSaving(true);

    const requests = rows.map((row) => ({
      pk_timing_id: String(row.pk_timing_id || "0"),
      day_name: row.day_name,
      shift_no: String(row.shift_no),

      open_time: row.is_closed === "1" ? "00:00:00" : toApiTime(row.open_time),

      close_time:
        row.is_closed === "1" ? "00:00:00" : toApiTime(row.close_time),

      is_closed: row.is_closed === "1" ? "1" : "0",
      is_24_hours: row.is_24_hours === "1" ? "1" : "0",
    }));

    // 1. Detailed timings save API
    const manageResult = await saveRestaurantTimingsV2({
      restaurantId: userID,
      requests,
    });

    if (!manageResult?.success) {
      toast.error(
        manageResult?.message || "Failed to save detailed restaurant timings",
      );
      return;
    }

    const activeRows = rows.filter((row) => row.is_closed !== "1");

    if (activeRows.length === 0) {
      toast.error("At least one day must remain open");
      return;
    }

    const firstActiveRow = activeRows[0];

    const closedDays = DAYS.filter((day) => {
      const dayRows = rows.filter((row) => row.day_name === day);

      return (
        dayRows.length > 0 && dayRows.every((row) => row.is_closed === "1")
      );
    });

    const summaryPayload = {
      opening:
        firstActiveRow.is_24_hours === "1" ? "00:00" : firstActiveRow.open_time,

      closing:
        firstActiveRow.is_24_hours === "1"
          ? "23:59"
          : firstActiveRow.close_time,

      weekOff: weekOff,
    };

    // 2. Main restaurant timing API
    const updateResult = await updateRestaurantTiming(summaryPayload);

    if (!updateResult?.success) {
      toast.error(
        updateResult?.message ||
          "Detailed timing saved, but main timing update failed",
      );
      return;
    }

    toast.success("Restaurant timings saved successfully");

    await loadTimings();
  } catch (error) {
    console.error("SAVE RESTAURANT TIMINGS ERROR:", error);
    toast.error("Failed to save restaurant timings");
  } finally {
    setSaving(false);
  }
};

  return (
    <>
    <div className="space-y-6 montserrat-font-1">
      {/* CURRENT TIMINGS */}

      {/* CURRENT TIMINGS */}

      <div className="rounded-2xl border bg-white p-6 shadow-sm">
        <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-green-700">
              Restaurant Timing Overview
            </h2>

            <p className="mt-1 text-sm text-gray-500">
              Default timing and day-wise restaurant schedule.
            </p>
          </div>

          <button
            type="button"
            onClick={loadTimings}
            disabled={loading}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />

            {loading ? "Refreshing..." : "Refresh"}
          </button>
        </div>

        {/* GET RESTAURANT TIMING API CARD */}

        <div className="mb-6 rounded-2xl border border-green-100 bg-green-50 p-4">
          <div className="mb-3 flex items-center justify-between">
            <div>
              <h3 className="text-sm font-bold text-gray-900">
                Default Restaurant Timing
              </h3>

              <p className="mt-1 text-xs text-gray-500">
                Data from getRestaurantTiming API
              </p>
            </div>

            <Clock size={18} className="text-green-600" />
          </div>

          {loading ? (
            <div className="text-sm text-gray-500">
              Loading restaurant timing...
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="rounded-xl border border-green-100 bg-white p-3">
                <div className="text-xs font-semibold text-gray-500">
                  Opening
                </div>

                <div className="mt-1 text-sm font-bold text-gray-900">
                  {restaurantTimingSummary.opening
                    ? to12HourLabel(restaurantTimingSummary.opening)
                    : "Not Set"}
                </div>
              </div>

              <div className="rounded-xl border border-green-100 bg-white p-3">
                <div className="text-xs font-semibold text-gray-500">
                  Closing
                </div>

                <div className="mt-1 text-sm font-bold text-gray-900">
                  {restaurantTimingSummary.closing
                    ? to12HourLabel(restaurantTimingSummary.closing)
                    : "Not Set"}
                </div>
              </div>

              <div className="rounded-xl border border-green-100 bg-white p-3">
                <div className="text-xs font-semibold text-gray-500">
                  Week Off
                </div>

                <div className="mt-1 text-sm font-bold text-gray-900">
                  {restaurantTimingSummary.weekOff || "No Week Off"}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* MANAGE RESTAURANT TIMINGS API RESPONSE */}

        <div className="mb-3">
          <h3 className="text-sm font-bold text-gray-900">
            Day-wise Restaurant Timings
          </h3>

          <p className="mt-1 text-xs text-gray-500">
            Data from manageRestuarantTimings API
          </p>
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {DAYS.map((day) => {
            const dayRows = groupedTimings[day] || [];

            const isClosed =
              dayRows.length > 0 &&
              dayRows.every((row) => row.is_closed === "1");

            return (
              <div
                key={day}
                className="rounded-2xl border border-gray-100 bg-gray-50 p-4"
              >
                <div className="mb-3 flex items-center justify-between">
                  <div className="text-sm font-bold text-gray-900">{day}</div>

                  {dayRows.length === 0 ? (
                    <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-500">
                      Not Set
                    </span>
                  ) : isClosed ? (
                    <span className="rounded-full bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-600">
                      Closed
                    </span>
                  ) : (
                    <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
                      Open
                    </span>
                  )}
                </div>

                {loading ? (
                  <div className="rounded-xl bg-white p-3 text-sm text-gray-500">
                    Loading...
                  </div>
                ) : dayRows.length === 0 ? (
                  <div className="rounded-xl bg-white p-3 text-sm text-gray-500">
                    No timing added
                  </div>
                ) : (
                  <div className="space-y-2">
                    {dayRows.map((item, index) => (
                      <div
                        key={`${day}-${item.shift_no}-${item.pk_timing_id}-${index}`}
                        className="flex items-center justify-between rounded-xl bg-white px-3 py-2"
                      >
                        <div>
                          <div className="text-xs font-semibold text-gray-500">
                            Shift {item.shift_no}
                          </div>

                          <div className="text-sm font-bold text-gray-900">
                            {item.is_closed === "1"
                              ? "Closed"
                              : item.is_24_hours === "1"
                                ? "24 Hours"
                                : `${to12HourLabel(
                                    item.open_time,
                                  )} - ${to12HourLabel(item.close_time)}`}
                          </div>
                        </div>

                        <Clock size={16} className="text-gray-400" />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* UPDATE TIMINGS */}

      <div className="rounded-2xl border bg-white p-6 shadow-sm">
        <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-green-700">
              Update Restaurant Timings
            </h2>

            <p className="mt-1 text-sm text-gray-500">
              Select restaurant timing and weekly off day.
            </p>
          </div>

          <button
            type="button"
            onClick={addRow}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white hover:bg-blue-700"
          >
            <Plus size={16} />
            Add Row
          </button>
        </div>

        {/* WEEK OFF DROPDOWN */}

        <div className="mb-5 max-w-sm">
          <label
            htmlFor="weekOff"
            className="mb-2 block text-sm font-semibold text-gray-700"
          >
            Week Off
          </label>

          <select
            id="weekOff"
            value={weekOff}
            onChange={(event) => handleWeekOffChange(event.target.value)}
            className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm outline-none transition focus:border-green-500 focus:ring-2 focus:ring-green-100"
          >
            <option value="">No Week Off</option>

            {DAYS.map((day) => (
              <option key={day} value={day}>
                {day}
              </option>
            ))}
          </select>

        </div>

        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-green-600 text-white">
                <tr>
                  <th className="whitespace-nowrap px-4 py-3 font-semibold">
                    Day
                  </th>

                  <th className="whitespace-nowrap px-4 py-3 font-semibold">
                    Shift
                  </th>

                  <th className="whitespace-nowrap px-4 py-3 font-semibold">
                    Open Time
                  </th>

                  <th className="whitespace-nowrap px-4 py-3 font-semibold">
                    Close Time
                  </th>

                  <th className="whitespace-nowrap px-4 py-3 font-semibold">
                    Closed
                  </th>

                  <th className="whitespace-nowrap px-4 py-3 font-semibold">
                    24 Hours
                  </th>

                  <th className="whitespace-nowrap px-4 py-3 text-right font-semibold">
                    Action
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-100">
                {rows.map((row, index) => {
                  const disabledTime =
                    row.is_closed === "1" || row.is_24_hours === "1";

                  return (
                    <tr
                      key={`${row.pk_timing_id}-${index}`}
                      className="hover:bg-gray-50"
                    >
                      <td className="min-w-[170px] px-4 py-3">
                        <select
                          value={row.day_name}
                          onChange={(event) =>
                            updateRow(index, "day_name", event.target.value)
                          }
                          className="w-full rounded-lg border px-3 py-2 text-sm outline-none focus:border-green-500"
                        >
                          {DAYS.map((day) => (
                            <option key={day} value={day}>
                              {day}
                            </option>
                          ))}
                        </select>
                      </td>

                      <td className="min-w-[110px] px-4 py-3">
                        <select
                          value={row.shift_no}
                          onChange={(event) =>
                            updateRow(index, "shift_no", event.target.value)
                          }
                          className="w-full rounded-lg border px-3 py-2 text-sm outline-none focus:border-green-500"
                        >
                          {SHIFTS.map((shift) => (
                            <option key={shift} value={shift}>
                              Shift {shift}
                            </option>
                          ))}
                        </select>
                      </td>

                      <td className="min-w-[140px] px-4 py-3">
                        <input
                          type="time"
                          value={row.open_time}
                          disabled={disabledTime}
                          onChange={(event) =>
                            updateRow(index, "open_time", event.target.value)
                          }
                          className="w-full rounded-lg border px-3 py-2 text-sm outline-none focus:border-green-500 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"
                        />
                      </td>

                      <td className="min-w-[140px] px-4 py-3">
                        <input
                          type="time"
                          value={row.close_time}
                          disabled={disabledTime}
                          onChange={(event) =>
                            updateRow(index, "close_time", event.target.value)
                          }
                          className="w-full rounded-lg border px-3 py-2 text-sm outline-none focus:border-green-500 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"
                        />
                      </td>

                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => {
                            const newValue = row.is_closed === "1" ? "0" : "1";

                            updateRow(index, "is_closed", newValue);

                            if (newValue === "1") {
                              setWeekOff(row.day_name);
                            } else if (weekOff === row.day_name) {
                              setWeekOff("");
                            }
                          }}
                          className={`rounded-full px-3 py-1 text-xs font-semibold ${
                            row.is_closed === "1"
                              ? "bg-red-100 text-red-700"
                              : "bg-gray-100 text-gray-600"
                          }`}
                        >
                          {row.is_closed === "1" ? "Yes" : "No"}
                        </button>
                      </td>

                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() =>
                            updateRow(
                              index,
                              "is_24_hours",
                              row.is_24_hours === "1" ? "0" : "1",
                            )
                          }
                          className={`rounded-full px-3 py-1 text-xs font-semibold ${
                            row.is_24_hours === "1"
                              ? "bg-emerald-100 text-emerald-700"
                              : "bg-gray-100 text-gray-600"
                          }`}
                        >
                          {row.is_24_hours === "1" ? "Yes" : "No"}
                        </button>
                      </td>

                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          onClick={() => removeRow(index)}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-red-100 bg-red-50 text-red-600 hover:bg-red-100"
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-xs text-gray-500">
            Opening aur closing first open row se jayenge. Selected weekly off{" "}
            <b>weekOff</b> field me jayega.
          </div>

          <button
            type="button"
            onClick={saveTimings}
            disabled={saving || loading}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-green-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Save size={16} />

            {saving ? "Saving..." : "Save Timings"}
          </button>
        </div>
      </div>
    </div>
    </>
  );
};

export default TimingSettings;

